import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

const parseBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }
  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }
  return fallback;
};

const envPilotModeEnabled = parseBoolean(process.env.REACT_APP_PILOT_MODE, true);
const defaultPilotFlags = Object.freeze({
  pilotModeEnabled: envPilotModeEnabled,
  hideOAuthButtons: parseBoolean(process.env.REACT_APP_HIDE_OAUTH_BUTTONS, envPilotModeEnabled),
  hideBillingUi: parseBoolean(process.env.REACT_APP_HIDE_BILLING_UI, false),
  mode: envPilotModeEnabled ? 'pilot' : 'live',
  updatedAt: null,
});

const normalizePilotFlags = (payload = {}, fallback = defaultPilotFlags) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const mode = typeof source.mode === 'string' ? source.mode.trim().toLowerCase() : '';
  const modeEnabled = mode === 'pilot'
    ? true
    : (mode === 'live' ? false : null);

  const hasExplicitEnabled = modeEnabled !== null
    || Object.prototype.hasOwnProperty.call(source, 'pilotModeEnabled')
    || Object.prototype.hasOwnProperty.call(source, 'enabled');
  const resolvedEnabled = modeEnabled !== null
    ? modeEnabled
    : (hasExplicitEnabled
      ? Boolean(source.pilotModeEnabled ?? source.enabled)
      : Boolean(fallback.pilotModeEnabled));

  const hideOAuthButtons = Object.prototype.hasOwnProperty.call(source, 'hideOAuthButtons')
    ? Boolean(source.hideOAuthButtons)
    : (hasExplicitEnabled ? resolvedEnabled : Boolean(fallback.hideOAuthButtons));
  const hideBillingUi = Object.prototype.hasOwnProperty.call(source, 'hideBillingUi')
    ? Boolean(source.hideBillingUi)
    : (hasExplicitEnabled ? resolvedEnabled : Boolean(fallback.hideBillingUi));

  return {
    pilotModeEnabled: resolvedEnabled,
    hideOAuthButtons,
    hideBillingUi,
    mode: resolvedEnabled ? 'pilot' : 'live',
    updatedAt: source.updatedAt || fallback.updatedAt || null,
  };
};

let cachedPilotFlags = { ...defaultPilotFlags };
let hasHydratedBootstrap = false;
let hasFetchedRemote = false;
let inFlightRequest = null;
const subscribers = new Set();

const notifySubscribers = () => {
  subscribers.forEach((listener) => {
    try {
      listener({ ...cachedPilotFlags });
    } catch {
      // Ignore subscriber errors.
    }
  });
};

const applyFlags = (next = {}) => {
  const normalized = normalizePilotFlags(next, cachedPilotFlags);
  cachedPilotFlags = normalized;
  Object.assign(pilotFlags, normalized);
  notifySubscribers();
  return { ...cachedPilotFlags };
};

const hydrateFromBootstrap = () => {
  if (hasHydratedBootstrap || typeof window === 'undefined') {
    return;
  }
  hasHydratedBootstrap = true;
  const bootstrapPilot = window.__APP_CONFIG__?.pilot || window.__APP_CONFIG__?.features?.pilot || null;
  if (bootstrapPilot && typeof bootstrapPilot === 'object') {
    applyFlags(bootstrapPilot);
  }
};

const extractRemotePilotFlags = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }
  if (payload.pilot && typeof payload.pilot === 'object') {
    return payload.pilot;
  }
  return payload;
};

const fetchRemotePilotFlags = async ({ force = false } = {}) => {
  hydrateFromBootstrap();

  if (process.env.NODE_ENV === 'test') {
    return { ...cachedPilotFlags };
  }

  if (!force) {
    if (hasFetchedRemote) {
      return { ...cachedPilotFlags };
    }
    if (inFlightRequest) {
      return inFlightRequest;
    }
  }

  inFlightRequest = api.get('/auth/signup-config')
    .then((response) => {
      const payload = response?.data || {};
      const pilotPayload = extractRemotePilotFlags(payload);
      const merged = applyFlags({
        ...pilotPayload,
        updatedAt: payload.updatedAt || pilotPayload.updatedAt || null,
      });
      hasFetchedRemote = true;
      return merged;
    })
    .catch(() => {
      hasFetchedRemote = false;
      return { ...cachedPilotFlags };
    })
    .finally(() => {
      inFlightRequest = null;
    });

  return inFlightRequest;
};

export const pilotFlags = { ...cachedPilotFlags };

export const usePilotFlags = ({ fetchOnMount = true } = {}) => {
  const [flags, setFlags] = useState(() => {
    hydrateFromBootstrap();
    return { ...cachedPilotFlags };
  });

  useEffect(() => {
    const handleUpdate = (next) => {
      setFlags(next);
    };
    subscribers.add(handleUpdate);
    if (fetchOnMount) {
      fetchRemotePilotFlags().then((next) => setFlags(next));
    }
    return () => {
      subscribers.delete(handleUpdate);
    };
  }, [fetchOnMount]);

  const refresh = useCallback(() => fetchRemotePilotFlags({ force: true }), []);

  return {
    ...flags,
    refresh,
  };
};

export const preloadPilotFlags = () => fetchRemotePilotFlags();
